import React, { useEffect } from 'react';
import { Box, Divider, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import LabeledInput from '../Common/LabeledInput';
import CustomPhoneNumberInput from '../CustomPhoneNumberInput/CustomPhoneNumberInput';
import {
  getValidationSchema,
  initialValues,
  FormValues,
} from '../../utils/validations/memberDetailsForm';
import AddNewCoachModal from '../AddNewCoach/AddNewCoachModal';
import StyledButton from '../Common/StyledButton';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { sanitizeInputName } from '../../utils/userInputUtils';
import {
  Role,
  addCoachToGroupRequest,
  editTeamMemberDataRequest,
  IAddCoachToGroupInput,
  IEditCoachToGroupInput,
  closeAddMemberModalOnSuccess,
} from '../../redux/coachGroup/coachGroupSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { brandTagKey } from '../../config/constants';
import { formatRole, formatRoleForDisplay } from '../../utils/utils';

interface IAddMemberProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit?: boolean;
  initialData?: Partial<FormValues> | null;
}

function AddTeamMemberModal({ isOpen, onClose, isEdit, initialData }: Readonly<IAddMemberProps>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { addCoachToGroupLoading, editCoachInoGroupLoading, isAddMemberSuccess } = useAppSelector(
    (state) => state.coachGroup
  );
  const brandTag = localStorage.getItem(brandTagKey);

  const handleAddMember = (values: FormValues) => {
    const { firstName, lastName, email, phoneNumber, role } = values;

    if (isEdit && initialData?.id) {
      const editMemberInput: IEditCoachToGroupInput = {
        id: initialData.id,
        firstName,
        lastName: lastName || '',
        email,
        tp: phoneNumber || '',
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
        role: formatRole(role),
        brandTag: brandTag || 'PGM',
      };
      dispatch(editTeamMemberDataRequest(editMemberInput));
      return;
    }
    const memberInput: IAddCoachToGroupInput = {
      firstName,
      lastName: lastName || '',
      email,
      tp: phoneNumber || '',
      organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
      role: formatRole(role),
      brandTag: brandTag || 'PGM',
    };
    dispatch(addCoachToGroupRequest(memberInput));
  };

  const formik = useFormik({
    initialValues: isEdit ? { ...initialValues, ...initialData } : initialValues,
    validationSchema: getValidationSchema(),
    onSubmit: handleAddMember,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (isOpen) {
      formik.resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isAddMemberSuccess) {
      onClose();
      dispatch(closeAddMemberModalOnSuccess());
    }
  }, [isAddMemberSuccess]);

  const handleEmailChange = (field: string, value: string) => {
    formik.handleChange(field)(value.toLowerCase());
  };

  const handleNameChange = (field: string, value: string) => {
    formik.handleChange(field)(sanitizeInputName(value));
  };

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('phoneNumber', pNumber);
  };

  const roleOptions = [
    {
      value: formatRoleForDisplay(Role.ASSISTANT_COACH),
      label: formatRoleForDisplay(Role.ASSISTANT_COACH),
    },
  ];

  return (
    <AddNewCoachModal isOpen={isOpen} key={isEdit ? 'edit' : 'add'} onClose={onClose}>
      <Grid item xs={12} bgcolor="background.paper" p="32px">
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid gap="36px" display="flex" flexDirection="column">
            {/* first row  */}
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h4" color="primary">
                {t(isEdit ? 'team.editMemberModalTitle' : 'team.addNewMemberModalTitle')}
              </Typography>
              <img
                src="/assets/icons/close-circle-large.svg"
                alt="close"
                onClick={onClose}
                role="presentation"
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Divider />
            {/* second row */}

            <Box gap={2} display="flex" flexDirection="column">
              {/* third row */}
              <Grid display="flex" gap="12px">
                <Grid item xs={6} mt="-3px">
                  <LabeledInput
                    label={`${t('addNewClient.firstName')}`}
                    id="first-name"
                    type="text"
                    name="firstName"
                    value={formik.values.firstName}
                    handleChange={(event) => handleNameChange('firstName', event.target.value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.firstName ? formik.errors.firstName : undefined}
                    placeholder={`${t('addNewClient.firstName')}`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <LabeledInput
                    label={`${t('addNewClient.lastName')}`}
                    id="last-name"
                    type="text"
                    name="lastName"
                    value={formik.values.lastName}
                    handleChange={(event) => handleNameChange('lastName', event.target.value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.lastName ? formik.errors.lastName : undefined}
                    placeholder={t('addNewClient.lastName')}
                  />
                </Grid>
              </Grid>
              {/* fourth row */}
              <Grid display="flex" gap="12px">
                <Grid item xs={6}>
                  <LabeledInput
                    label={`${t('addNewClient.email')}`}
                    id="email"
                    type="email"
                    value={formik.values.email}
                    handleChange={(event) => handleEmailChange('email', event.target.value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email ? formik.errors.email : undefined}
                    placeholder={t('addNewClient.email')}
                    disabled={isEdit && !!initialData?.email}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomPhoneNumberInput
                    name="phoneNumber"
                    value={formik.values.phoneNumber as string}
                    handleChange={handlePhoneNumberChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phoneNumber ? formik.errors.phoneNumber : undefined}
                    optional
                  />
                </Grid>
              </Grid>
              {/* fifth row */}
              <Box display="flex" gap="12px">
                <Grid item xs={6}>
                  <CustomDropdown
                    name="role"
                    options={roleOptions}
                    label={`${t('team.addNewMemberModalRole')}`}
                    value={formik.values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.role ? formik.errors.role : undefined}
                    disabled={isEdit && !!initialData?.role}
                  />
                </Grid>
              </Box>
            </Box>

            {/* seventh row */}
            <Divider />
            <Box display="flex" flexDirection="row-reverse">
              <StyledButton
                loading={addCoachToGroupLoading || editCoachInoGroupLoading}
                loadingText={t('team.addNewMemberModalAddButtonLoading')}
              >
                {t('team.addNewMemberModalAddButton')}
              </StyledButton>
            </Box>
          </Grid>
        </form>
      </Grid>
    </AddNewCoachModal>
  );
}

export default AddTeamMemberModal;
